/**
 * Background Task Notifications Component
 * Displays notifications about ongoing and completed background tasks
 * Shows across the entire app regardless of navigation
 */

import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import backgroundTaskManager from '../utils/backgroundTasks';
import { showToast } from './Toast';

const BackgroundTaskNotifications = () => {
  const { user } = useAuth();
  const { apiClient } = useAPI();
  const [activeTasks, setActiveTasks] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  // Listen for story-generation-failed events from CreateStory or backend
useEffect(() => {
  const handleStoryFail = (e) => {
    showToast("Oops! The story didn’t load this time. Let’s try again!", "error");
  };

  window.addEventListener("story-generation-failed", handleStoryFail);

  return () => {
    window.removeEventListener("story-generation-failed", handleStoryFail);
  };
}, []);

  useEffect(() => {
    if (!user?.id) return;

    let pollInterval;

    const initializeTaskMonitoring = async () => {
      try {
        // Get initial pending tasks
        const pendingTasks = await backgroundTaskManager.getPendingTasks(user.id);
        setActiveTasks(pendingTasks);
        setIsVisible(pendingTasks.length > 0);

        // Start polling for task updates every 5 seconds
        pollInterval = setInterval(async () => {
          try {
            const currentTasks = await backgroundTaskManager.getPendingTasks(user.id);

            // Check for newly completed tasks
            activeTasks.forEach(oldTask => {
              const currentTask = currentTasks.find(t => t.id === oldTask.id);
              if (!currentTask || currentTask.status !== oldTask.status) {
                handleTaskStatusChange(oldTask, currentTask);
              }
            });

            setActiveTasks(currentTasks);
            setIsVisible(currentTasks.length > 0);
          } catch (error) {
            logger.error('Error polling background tasks:', error);
          }
        }, 5000);

      } catch (error) {
        logger.error('Error initializing task monitoring:', error);
      }
    };

    initializeTaskMonitoring();

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [user?.id, activeTasks]);

  const handleTaskStatusChange = (oldTask, newTask) => {
    if (!newTask) {
      // Task was completed or removed
      if (oldTask.status === 'pending' || oldTask.status === 'processing') {
        // Check the actual task status from DB
        backgroundTaskManager.getTaskStatus(oldTask.id).then(task => {
          if (task?.status === 'completed') {
            showTaskCompletedNotification(task);
          } else if (task?.status === 'failed') {
            showTaskFailedNotification(task);
          }
        });
      }
    } else if (newTask.status !== oldTask.status) {
      // Status changed
      if (newTask.status === 'completed') {
        showTaskCompletedNotification(newTask);
      } else if (newTask.status === 'failed') {
        showTaskFailedNotification(newTask);
      }
    }
  };

  const showTaskCompletedNotification = (task) => {
    switch (task.type) {
      case 'character_creation':
        showToast(`✅ Character "${task.data.name}" was created successfully!`, 'success');
        
        // Add CREATECHARACTER_<CHARACTER_ID> tag when character is created in background (only for signed-in users)
        if (user?.id && apiClient && task.result) {
          // Extract character ID from result (could be in data array or direct)
          const characterId = Array.isArray(task.result) ? task.result[0]?.id : (task.result?.data?.id || task.result?.id);
          if (characterId) {
            const tag = `CREATECHARACTER_${characterId}`;
            apiClient.addUserDataTag(user.id, tag).then((result) => {
              if (result.status) {
                logger.log(`✅ Added tag ${tag} for user ${user.id}`);
              } else {
                logger.warn(`⚠️ Failed to add tag ${tag}:`, result.error);
              }
            }).catch((error) => {
              logger.error(`❌ Error adding tag ${tag}:`, error);
            });
          }
        }
        break;
      case 'show_creation':
        showToast(`✅ Show "${task.data.name}" was created successfully!`, 'success');
        break;
      case 'episode_creation':
        showToast(`✅ Episode was created successfully!`, 'success');
        break;
      default:
        showToast('✅ Background task completed successfully!', 'success');
    }
  };

  const showTaskFailedNotification = (task) => {
    const errorMsg = task.error || 'Unknown error occurred';
    switch (task.type) {
      case 'character_creation':
        showToast(`❌ Character creation failed: ${errorMsg}`, 'error');
        break;
      case 'show_creation':
        showToast(`❌ Show creation failed: ${errorMsg}`, 'error');
        break;
      case 'episode_creation':
        showToast(`❌ Episode creation failed: ${errorMsg}`, 'error');
        break;
      default:
        showToast(`❌ Background task failed: ${errorMsg}`, 'error');
    }
  };

  const getTaskDisplayName = (task) => {
    switch (task.type) {
      case 'character_creation':
        return `Creating character "${task.data.name || 'Unnamed'}"`;
      case 'show_creation':
        return `Creating show "${task.data.name || 'Unnamed'}"`;
      case 'episode_creation':
        return 'Creating episode';
      default:
        return 'Processing...';
    }
  };

  const getTaskIcon = (task) => {
    switch (task.type) {
      case 'character_creation':
        return '🎨';
      case 'show_creation':
        return '📺';
      case 'episode_creation':
        return '🎬';
      default:
        return '⏳';
    }
  };

  const handleRetryTask = async (taskId) => {
    try {
      await backgroundTaskManager.retryTask(taskId);
      showToast('Task retry initiated', 'info');
    } catch (error) {
      showToast(`Failed to retry task: ${error.message}`, 'error');
    }
  };

  const handleCancelTask = async (taskId) => {
    try {
      await backgroundTaskManager.cancelTask(taskId);
      setActiveTasks(prev => prev.filter(task => task.id !== taskId));
      showToast('Task cancelled', 'info');
    } catch (error) {
      showToast(`Failed to cancel task: ${error.message}`, 'error');
    }
  };

  if (!isVisible || activeTasks.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            Background Tasks ({activeTasks.length})
          </h3>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {activeTasks.map(task => (
            <div key={task.id} className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-2">
                  <span className="text-lg">{getTaskIcon(task)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                      {getTaskDisplayName(task)}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className={`text-xs px-2 py-1 rounded-full ${
                        task.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        task.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        task.status === 'completed' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {task.status === 'pending' ? 'Queued' :
                         task.status === 'processing' ? 'Processing' :
                         task.status === 'completed' ? 'Completed' :
                         'Failed'}
                      </div>
                      {task.status === 'processing' && (
                        <div className="flex space-x-1">
                          <div className="w-1 h-1 bg-blue-600 rounded-full animate-pulse"></div>
                          <div className="w-1 h-1 bg-blue-600 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                          <div className="w-1 h-1 bg-blue-600 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                        </div>
                      )}
                    </div>
                    {task.error && (
                      <p className="text-xs text-red-600 mt-1 truncate" title={task.error}>
                        {task.error}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col space-y-1 ml-2">
                  {task.status === 'failed' && (
                    <button
                      onClick={() => handleRetryTask(task.id)}
                      className="text-xs text-blue-600 hover:text-blue-800 px-1"
                      title="Retry task"
                    >
                      🔄
                    </button>
                  )}
                  {(task.status === 'pending' || task.status === 'failed') && (
                    <button
                      onClick={() => handleCancelTask(task.id)}
                      className="text-xs text-red-600 hover:text-red-800 px-1"
                      title="Cancel task"
                    >
                      ❌
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 rounded-b-lg">
          <p className="text-xs text-gray-600 dark:text-gray-300">
            Tasks continue even if you close this tab
          </p>
        </div>
      </div>
    </div>
  );
};

export default BackgroundTaskNotifications;